
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Sun } from 'lucide-react';
import useAuth from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';

const LoginPage = () => {
  const {
    login,
    user,
    isAdmin,
    loading,
    authChangeProcessed,
    session
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Redirect if already logged in - removed !isLoggingIn condition
  useEffect(() => {
    if (authChangeProcessed && user) {
      const redirectPath = isAdmin ? '/admin/dashboard' : '/dashboard';
      setIsLoggingIn(false); // Reset the logging in state
      navigate(redirectPath, { replace: true });
    }
  }, [user, isAdmin, navigate, authChangeProcessed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const success = await login(email, password);
      if (!success) {
        setIsLoggingIn(false);
      }
      // If success, the auth state change will trigger the redirect
      // and we reset isLoggingIn in the useEffect above
    } catch (error) {
      console.error('Login error:', error);
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-warm-cream">
      <NavBar />
      
      <main className="flex-1 flex items-center justify-center py-12">
        <Card className="w-full max-w-md border-peach-orange/20">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-2">
              <Sun className="h-10 w-10 text-peach-orange" />
            </div>
            <CardTitle className="text-2xl font-bold text-center">Welcome Back</CardTitle>
            <CardDescription className="text-center">
              Sign in to your account to view your sunny stats and favorites
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="hello@example.com" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Button variant="link" className="p-0 h-auto text-xs">
                    Forgot password?
                  </Button>
                </div>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={e => setPassword(e.target.value)} 
                  required 
                />
              </div>
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || isLoggingIn}
              >
                {isLoggingIn ? "Signing in..." : "Sign In"}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="text-center text-sm">
              Want early access?{" "}
              <Button variant="link" asChild className="p-0 h-auto">
                <Link to="/waitlist">Join our waitlist</Link>
              </Button>
            </div>
          </CardFooter>
        </Card>
      </main>
    </div>
  );
};

export default LoginPage;
